import * as _4 from 'lodash4';
import { FastConditionalStrategy, Strategy } from 'utility-ts-strategy';
import { IPlanFeedbackCourseGroup } from '../builders/plan_feedback_course_group_builder';
import { PlanFeedbackProgramSlot } from '../builders/plan_feedback_program_slot_builder';
import { IPlanFeedbackSuggestion } from '../builders/plan_feedback_transaction_type';
import { IPlanFeedbackProgramSlot } from './../builders/plan_feedback_course_builder';
import { FlaggedPlanFeedbackCourse, PlanFeedbackServant } from './../servants/plan_feedback_servant';

const ApSuggestionFlagTransactionTextStrategy = new FastConditionalStrategy([
  [
    'removed flag',
    (suggestion: IPlanFeedbackSuggestion) => !_4.isEmpty(suggestion.from_comment) && _4.isEmpty(suggestion.comment)
  ],
  [
    'flagged',
    (suggestion: IPlanFeedbackSuggestion) => _4.isEmpty(suggestion.from_comment) && !_4.isEmpty(suggestion.comment)
  ],
  ['', (suggestion: IPlanFeedbackSuggestion) => _4.isEmpty(suggestion.from_comment) && _4.isEmpty(suggestion.comment)],
  [
    'updated flag',
    (suggestion: IPlanFeedbackSuggestion) =>
      !_4.isEmpty(suggestion.from_comment) &&
      !_4.isEmpty(suggestion.comment) &&
      !_4.isEqual(suggestion.from_comment, suggestion.comment)
  ]
]);

const termField = '_term.name';
const fromTermField = '_from_term.name';

const ApSuggestionLockTransactionTextStrategy = new Strategy({
  REMOVE: (suggestion: IPlanFeedbackSuggestion) =>
    suggestion.lock_modified ? (suggestion.is_locked ? '' : 'unlocked') : '',
  ADD: (suggestion: IPlanFeedbackSuggestion) => (suggestion.lock_modified ? 'locked' : ''),
  NONE: (suggestion: IPlanFeedbackSuggestion) =>
    suggestion.lock_modified ? (suggestion.is_locked ? '' : 'un') + 'locked' : '',
  MOVE: (suggestion: IPlanFeedbackSuggestion) =>
    suggestion.lock_modified ? (suggestion.is_locked ? '' : 'un') + 'locked' : ''
});

const processNoneFeedbackSuggestion = (suggestion: IPlanFeedbackSuggestion) => {
  const emptyTransactionText = ``;
  if (!PlanFeedbackServant.isNoneSuggestion(suggestion)) {
    return emptyTransactionText;
  }

  if (PlanFeedbackServant.isNoneSuggestion(suggestion)) {
    const noChangeInTransaction =
      (_4.isEmpty(suggestion.from_comment) && _4.isEmpty(suggestion.comment)) ||
      suggestion.from_comment === suggestion.comment;
    if (noChangeInTransaction) {
      return emptyTransactionText;
    } else {
      return `reason changed`;
    }
  }

  return emptyTransactionText;
};

const ApPlanFeedbackDiffTextStrategy = new Strategy<(suggestion: IPlanFeedbackSuggestion) => string>({
  MET: (suggestion: IPlanFeedbackSuggestion) => `marked as met`,
  UNMET: (suggestion: IPlanFeedbackSuggestion) => `marked as unmet`,
  REMOVE: (suggestion: IPlanFeedbackSuggestion) => `removed`,
  ADD: (suggestion: IPlanFeedbackSuggestion) => `added`,
  NONE: (suggestion: IPlanFeedbackSuggestion) => processNoneFeedbackSuggestion(suggestion),
  MOVE: (suggestion: IPlanFeedbackSuggestion) => {
    const placeHolderChanged =
      _4.get(suggestion.from_program_slot_obj as IPlanFeedbackProgramSlot, 'id') !==
      _4.get(suggestion.program_slot_obj, 'id');
    const placeHolderText = placeHolderChanged ? `into ${_4.get(suggestion.program_slot_obj, 'desc')}` : ``;
    return `moved to ${_4.get(suggestion, termField)} ${placeHolderText}`;
  }
});

const ApPlanSuggestionSourceTermInfo = new Strategy({
  REMOVE: (suggestion: IPlanFeedbackSuggestion) => `from ${_4.get(suggestion, fromTermField)}`,
  ADD: (suggestion: IPlanFeedbackSuggestion) => `to ${_4.get(suggestion, termField)}`,
  NONE: (suggestion: IPlanFeedbackSuggestion) => `in ${_4.get(suggestion, termField)}`,
  MOVE: (suggestion: IPlanFeedbackSuggestion) => `from ${_4.get(suggestion, fromTermField)}`
});

export const ApPlanSuggestionTransactionTextStrategy = new Strategy({
  COURSE: (feedbackCourse: FlaggedPlanFeedbackCourse, includeSourceTermInfo: boolean = false) => {
    const flagText = ApSuggestionFlagTransactionTextStrategy.evaluate(feedbackCourse);
    const lockText = ApSuggestionLockTransactionTextStrategy[feedbackCourse.diff](feedbackCourse);
    const courseText = `<span class='text-bold'> ${feedbackCourse.course.cd} </span>`;
    const diffText = ApPlanFeedbackDiffTextStrategy[feedbackCourse.diff](feedbackCourse);

    let transactionText = ``;
    if (!_4.isEmpty(lockText) && !_4.isEmpty(flagText)) {
      transactionText = `${lockText} and ${flagText}`;
    }
    if (!_4.isEmpty(lockText) && _4.isEmpty(flagText)) {
      transactionText = `${lockText}`;
    }
    if (_4.isEmpty(lockText) && !_4.isEmpty(flagText)) {
      transactionText = `${flagText}`;
    }
    transactionText = _4.isEmpty(diffText)
      ? transactionText
      : !_4.isEmpty(transactionText)
      ? `${transactionText} and ${diffText}`
      : `${diffText}`;

    if (includeSourceTermInfo) {
      transactionText = transactionText + ` ${ApPlanSuggestionSourceTermInfo[feedbackCourse.diff](feedbackCourse)}`;
    }
    return `${courseText}${transactionText}`;
  },
  PROGRAM_SLOT: (planFeedbackProgramSlot: PlanFeedbackProgramSlot) => `
    <span class='text-bold'>
      ${PlanFeedbackServant.getTextForFeedbackProgramSlot(planFeedbackProgramSlot)}
    </span>
    ${ApPlanFeedbackDiffTextStrategy[planFeedbackProgramSlot.diff](planFeedbackProgramSlot)}
  `,
  COURSE_GROUP: (feedbackCourseGroup: IPlanFeedbackCourseGroup, includeSourceTermInfo: boolean = false) => `
        <span class='text-bold'>
            ${
              !_4.isEmpty(feedbackCourseGroup.program_slot_obj.desc)
                ? feedbackCourseGroup.program_slot_obj.desc
                : 'Course Group'
            }
        </span>
        ${ApPlanFeedbackDiffTextStrategy[feedbackCourseGroup.diff](feedbackCourseGroup)} 
        ${includeSourceTermInfo ? ApPlanSuggestionSourceTermInfo[feedbackCourseGroup.diff](feedbackCourseGroup) : ''}
        `
});



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/strategies/plan_feedback_transaction_text.ts